import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Ticket } from '@models/Ticket';
import { TicketDetail } from '@models/TicketDetail';
import { User } from '@models/User';
import { AuthService } from '@services/auth.service';
import { TicketService } from '@services/ticket.service';
import { SnackbarService } from '@services/support/snackbar.service';
import { UtilityService } from '@services/remote/utility.service';
import { RoomService } from '@services/remote/room.service';
import { Subscription } from 'rxjs';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TicketDetailComponent } from './ticket-detail/ticket-detail.component';
import { LoaderService } from '@services/support/loader.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertModalComponent } from 'src/app/components/modals/alert-modal/alert-modal.component';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
    selector: 'app-tickets',
    templateUrl: './tickets.component.html',
    styleUrls: ['./tickets.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TicketsComponent implements OnInit, OnDestroy {

  mode: string = null;
  tabIndex = 0;

  timezone = "UTC";

  ticketStatuses: any[] = [{ id: "open", name: "Open" }, { id: "in-progress", name: "In Progress" }, { id: "closed", name: "Closed" }];

  filteredTickets: any[] = [];
  userTickets: Ticket[];

  isExpert: boolean = null;
  isAdmin: boolean = null;

  rooms: any[] = [];

  filterStartEnd: Date[] = null;
  filterRooms: string[] = [];
  filterStatuses: string[] = [];
  filterCreators: string[] = [];
  filterAssignees: string[] = [];

  selectedTicket: Ticket = null;

  creatorFilterList;
  assigneeFilterList;

  isLoading = false;

  // Sort orders
  titleOrder: string = null;
  dateOrder: string = null;

  private subscriptions: Subscription[] = [];

  constructor(
    private authService: AuthService,
    private snackbarService: SnackbarService,
    private ticketService: TicketService,
    private modal: BsModalService,
    private loaderService: LoaderService,
    private translateService: TranslateService,
    private roomService: RoomService
  ) { }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  ngOnInit(): void {
    this.isExpert = this.authService.currentUser?.role === 'expert';
    this.isAdmin = this.roomService.hasFullRoomAccess();

    // Admin ve co-admin kullanıcıları için tüm odaları al, normal kullanıcılar için sadece kendi odalarını
    if (this.isAdmin) {
      this.subscriptions.push(this.roomService.allRooms.subscribe(rooms => {
        if (rooms) {
          this.rooms = rooms
            .filter(r => r.room_data && !r.room_data.deleted && !r.room_data.personal_room && !r.room_data.meeting_room)
            .map(r => ({ id: r.id, name: r.room_data.name }))
            .sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
        }
      }));
    } else {
      this.subscriptions.push(this.authService.user.subscribe(user => {
        if (user) {
          this.timezone = "UTC";
          this.rooms = ((user && user.rooms) ? Object.keys(user.rooms).map(rid => { return { id: rid, name: user.rooms[rid].name }; }) : [])
            .sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
        }
      }));
    }

    // Set default sort order
    this.dateOrder = 'desc';

    this.getTickets();
  }

  onTabClicked(index: number) {
    this.tabIndex = index;
    this.onFilterChanged();
  }

  selectTicket(index: number) {
    this.selectedTicket = this.filteredTickets[index];
    this.mode = 'view';
  }

  selectedTicketChange(event) {
    if (!event) {
      this.selectedTicket = null;
      this.mode = null;
    }
  }

  onCreateTicket(): void {
    this.selectedTicket = null;
    this.mode = 'create';
  }

  ticketCreated(ticket: Ticket) {
    this.selectedTicket = null;
    this.mode = null;
    this.getTickets();
  }

  ticketUpdated(ticket: Ticket) {
    // Update the ticket in the list with new data
    const index = this.userTickets.findIndex(t => t.id === ticket.id);
    if (index > -1) {
      this.userTickets[index] = { ...this.userTickets[index], ...ticket };
      this.onFilterChanged();
    }
    
    // Keep the ticket detail open, just update the data
    // Don't close it: this.selectedTicket = null; this.mode = null;
  }

  changeStatus(status: string, index: number) {
    const ticket = this.filteredTickets[index];
    this.loaderService.show();
    
    const changes = {
      id: ticket.id,
      properties: { status: status }
    };
    
    this.ticketService.updateTicket(changes)
      .then(() => {
        this.loaderService.hide();
        this.getTickets();
        this.snackbarService.success(this.translateService.instant('MAIN.CONTENT.TICKETS.STATUS_UPDATED'));
      })
      .catch((error) => {
        this.loaderService.hide();
        this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.UPDATE_ERROR'));
      });
  }

  deleteTicket(ticketId: string) {
    const initialState = {
      headerTitle: this.translateService.instant('MAIN.CONTENT.TICKETS.DELETE'),
      textMessage: this.translateService.instant('MAIN.CONTENT.TICKETS.DELETE_CONFIRM'),
      confirmButtonText: this.translateService.instant('MAIN.CONTENT.TICKETS.DELETE')
    };
    let alertmodalRef = this.modal.show(AlertModalComponent, {
      initialState,
      backdrop: 'static',
      class: 'modal-dialog-centered',
      animated: false
    });
    alertmodalRef.content.onClose.subscribe(res => {
      if (res) {
        this.loaderService.show();
        this.ticketService.deleteTicket(ticketId)
          .then(() => {
            this.loaderService.hide();
            this.selectedTicket = null;
            this.mode = null;
            this.getTickets();
            this.snackbarService.success(this.translateService.instant('MAIN.CONTENT.TICKETS.DELETED'));
          })
          .catch((error) => {
            this.loaderService.hide();
            this.snackbarService.error(this.translateService.instant('MAIN.CONTENT.TICKETS.DELETE_ERROR'));
          });
      } else {
        alertmodalRef.hide();
      }
    });
  }

  sortByTitle() {
    this.dateOrder = null;
    
    if (this.titleOrder === 'asc') {
      this.titleOrder = 'desc';
      this.filteredTickets.sort((a, b) => b.title.localeCompare(a.title));
    } else {
      this.titleOrder = 'asc';
      this.filteredTickets.sort((a, b) => a.title.localeCompare(b.title));
    }
  }

  sortByDate() {
    this.titleOrder = null;
    
    if (this.dateOrder === 'asc') {
      this.dateOrder = 'desc';
      this.filteredTickets.sort((a, b) => b.create_time - a.create_time);
    } else {
      this.dateOrder = 'asc';
      this.filteredTickets.sort((a, b) => a.create_time - b.create_time);
    }
  }

  onFilterChanged(): void {
    let filtered = [...this.userTickets];

    // Tab filter (active/closed)
    if (this.tabIndex === 0) {
      filtered = filtered.filter(t => t.status !== 'closed');
    } else {
      filtered = filtered.filter(t => t.status === 'closed');
    }

    // Room filter
    if (this.filterRooms.length > 0) {
      filtered = filtered.filter(t => this.filterRooms.includes(t.room.id));
    }

    // Status filter
    if (this.filterStatuses.length > 0) {
      filtered = filtered.filter(t => this.filterStatuses.includes(t.status));
    }

    // Creator filter
    if (this.filterCreators.length > 0) {
      filtered = filtered.filter(t => this.filterCreators.includes(t.creator?.name));
    }

    // Assignee filter
    if (this.filterAssignees.length > 0) {
      filtered = filtered.filter(t => t.assignee && this.filterAssignees.includes(t.assignee.name));
    }

    // Date range filter
    if (this.filterStartEnd && this.filterStartEnd.length === 2) {
      const start = new Date(this.filterStartEnd[0].getTime());
      const end = new Date(this.filterStartEnd[1].getTime());
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      const startTime = start.getTime() - UtilityService.timezoneOffset - UtilityService.timezones[this.timezone];
      const endTime = end.getTime() - UtilityService.timezoneOffset - UtilityService.timezones[this.timezone];
      filtered = filtered.filter(t => startTime < t.create_time && t.create_time < endTime);
    }

    // Apply current sort order
    if (this.dateOrder === 'desc') {
      filtered.sort((a, b) => b.create_time - a.create_time);
    } else if (this.dateOrder === 'asc') {
      filtered.sort((a, b) => a.create_time - b.create_time);
    } else if (this.titleOrder === 'asc') {
      filtered.sort((a, b) => a.title.localeCompare(b.title));
    } else if (this.titleOrder === 'desc') {
      filtered.sort((a, b) => b.title.localeCompare(a.title));
    }

    this.filteredTickets = filtered;
  }

  getTickets(): void {
    this.isLoading = true;
    this.loaderService.show();
    this.ticketService.getTickets()
      .then(res => {
        this.isLoading = false;
        this.loaderService.hide();
        
        // Note: API does not return priority in ticket list, only in detail
        // Priority will be updated when ticket detail is loaded
        this.userTickets = res.tickets.map(ticket => {
          // Set default priority if not present
          if (!ticket.priority) {
            ticket.priority = 'low';
          }
          return ticket;
        }).sort((a, b) => b.create_time - a.create_time);
        
        const uniqueCreators = new Set();
        const uniqueAssignees = new Set();
        this.userTickets.forEach((ticketItem) => {
          uniqueCreators.add(ticketItem.creator.name);
          if (ticketItem.assignee) {
            uniqueAssignees.add(ticketItem.assignee.name);
          }
        });
        this.creatorFilterList = Array.from(uniqueCreators).sort((a: any, b: any) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()));
        this.assigneeFilterList = Array.from(uniqueAssignees).sort((a: any, b: any) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()));
        this.onFilterChanged();
      })
      .catch((error) => {
        this.isLoading = false;
        this.loaderService.hide();
        if (error.message === "permission-denied") {
          this.snackbarService.error(this.translateService.instant("APP.EXTERNALS.AD_ERROR.PERMISSION_DENIED"));
        }
        if (error.message === "internal-error") {
          this.snackbarService.error(this.translateService.instant("APP.EXTERNALS.AD_ERROR.INTERNAL_ERROR"));
        }
      });
  }

  getRoomName(id: string) {
    // Önce rooms listesinde ara
    const room = this.rooms.find(r => r.id === id);
    if (room) {
      return room.name;
    }
    
    // Rooms listesinde yoksa, ticket'ten al (admin için gerekli)
    const ticket = this.userTickets?.find(t => t.room_id === id || t.room?.id === id);
    if (ticket && ticket.room) {
      return ticket.room.name;
    }
    
    return "Unknown";
  }

  formatDate(timestamp: number): string {
    const d = new Date(timestamp + UtilityService.timezoneOffset + UtilityService.timezones[this.timezone]);
    return `${("0"+d.getDate()).slice(-2)}.${("0"+(d.getMonth()+1)).slice(-2)}.${d.getFullYear()} ${("0" + d.getHours()).slice(-2)}:${("0" + d.getMinutes()).slice(-2)}`;
  }
}
